import React from 'react';
import { GhostRenderer } from '../../rendering/GhostRenderer';
import { appearanceForClass } from '../../rendering/AppearancePresets';
import { withPlayerLook } from '../../rendering/playerLook';
import { DEFAULT_GHOST, type GhostAppearance } from '../../../shared/types/AppearanceTypes';
import { resolvePlayerEquipmentVisuals, type ResolvedPlayerEquipmentVisuals } from '../../../shared/equipmentVisuals';
import { normalizePortraitSize, preparePortraitSvg } from '../../rendering/svg/scopeSvgIds';

export interface CharacterPortraitProps {
  appearance?: Partial<GhostAppearance> | null;
  characterClass?: string;
  /** Always supplied by the item resolver; creation passes its empty loadout. */
  equipmentVisuals: ResolvedPlayerEquipmentVisuals;
  size?: number;
  label?: string;
}

const COSMETIC_OPTIONS = {
  eyes: ['round', 'happy', 'angry', 'winking', 'sleepy', 'stars'],
  mouth: ['smile', 'o', 'neutral', 'grin', 'frown'],
  shape: ['classic', 'round', 'slim', 'wide'],
  aura: ['none', 'flame', 'frost', 'shadow', 'holy'],
  accessory: ['none', 'bow', 'hat', 'crown', 'glasses', 'horns', 'halo', 'bandana'],
  faceMarking: ['none', 'scar', 'tattoo', 'freckles'],
  trail: ['none', 'sparkles', 'smoke', 'petals'],
  bodySize: ['small', 'normal', 'large'],
  opacity: ['solid', 'ghostly', 'ethereal'],
} as const;
const EMPTY_EQUIPMENT = resolvePlayerEquipmentVisuals({});

/** Keep cosmetics separate from legacy gear and renderer-only overrides. */
function portraitAppearance(appearance: CharacterPortraitProps['appearance'], characterClass?: string): GhostAppearance {
  const base = appearance ?? appearanceForClass(characterClass);
  const schema = base as Partial<GhostAppearance> & { toJSON?: () => Partial<GhostAppearance> };
  const source = typeof schema.toJSON === 'function' ? schema.toJSON() : base;
  const result: GhostAppearance = {
    color: DEFAULT_GHOST.color, eyes: DEFAULT_GHOST.eyes, mouth: DEFAULT_GHOST.mouth,
    shape: DEFAULT_GHOST.shape, aura: DEFAULT_GHOST.aura, accessory: DEFAULT_GHOST.accessory,
  };
  // Saved colors enter an SVG attribute. Accept CSS hex/named colors while
  // rejecting markup and malformed values before they reach the renderer.
  if (typeof source.color === 'string'
    && /^(?:#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})|[a-z]+)$/i.test(source.color)) {
    result.color = source.color;
  }
  for (const key of Object.keys(COSMETIC_OPTIONS) as Array<keyof typeof COSMETIC_OPTIONS>) {
    const value = source[key];
    if (typeof value === 'string' && (COSMETIC_OPTIONS[key] as readonly string[]).includes(value)) {
      (result as unknown as Record<string, unknown>)[key] = value;
    }
  }
  for (const key of ['blush', 'glow', 'actionPose'] as const) {
    if (typeof source[key] === 'boolean') result[key] = source[key];
  }
  return result;
}

/** The game's real SVG renderer, with cosmetics and item authority composed once. */
export const CharacterPortrait: React.FC<CharacterPortraitProps> = ({
  appearance, characterClass, equipmentVisuals, size = 200, label = 'Character portrait',
}) => {
  const namespace = `portrait-${React.useId().replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const renderSize = normalizePortraitSize(size);
  const svg = preparePortraitSvg(GhostRenderer.render({
    ...withPlayerLook(portraitAppearance(appearance, characterClass)),
    equipmentVisuals: equipmentVisuals ?? EMPTY_EQUIPMENT,
  }, renderSize), namespace);

  return <div
    className="character-portrait ghost-generator"
    role="img"
    aria-label={label}
    style={{ width: renderSize, height: renderSize }}
    dangerouslySetInnerHTML={{ __html: svg }}
  />;
};
